import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const ScriptsPage = () => {
  const { getToken } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => {
    fetchScripts();
  }, []);

  const fetchScripts = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/scripts`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setScripts(data);
      }
    } catch (error) {
      console.error('Error fetching scripts:', error);
    }
  };

  const handleCopy = (scriptId, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(scriptId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleExpanded = (scriptId) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(scriptId)) {
        newSet.delete(scriptId);
      } else {
        newSet.add(scriptId);
      }
      return newSet;
    });
  };

  const categories = [
    { value: 'all', label: 'All Scripts', icon: '📋' },
    { value: 'cold-call', label: 'Cold Calling', icon: '📞' },
    { value: 'inbound', label: 'Inbound Inquiry', icon: '📥' },
    { value: 'objection', label: 'Objection Handling', icon: '🛡️' },
    { value: 'follow-up', label: 'Follow-up', icon: '🔄' },
    { value: 'closing', label: 'Closing', icon: '✅' }
  ];

  const filteredScripts = selectedCategory === 'all'
    ? scripts
    : scripts.filter(s => s.category === selectedCategory);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-nunito text-white">Sales Scripts</h1>
        <p className="mt-1 text-orange-300 font-nunito">
          Pre-written scripts for various sales scenarios
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-3">
        {categories.map((cat) => (
          <button
            key={cat.value}
            onClick={() => setSelectedCategory(cat.value)}
            className={`px-4 py-2 rounded-lg font-nunito font-medium transition flex items-center gap-2 ${
              selectedCategory === cat.value
                ? 'bg-amber-500 text-black'
                : 'bg-orange-900/40 border border-white/10 text-orange-100 hover:bg-white/10'
            }`}
          >
            <span>{cat.icon}</span>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Scripts Grid */}
      <div className="grid grid-cols-1 gap-6">
        {filteredScripts.map((script) => {
          const isExpanded = expandedIds.has(script.id);
          return (
            <div key={script.id} className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleExpanded(script.id)}
                      className="text-amber-500 hover:text-amber-400 transition"
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <span className="text-2xl">{isExpanded ? '▼' : '▶'}</span>
                    </button>
                    <div>
                      <h3 className="text-xl font-bold text-white font-nunito">{script.title}</h3>
                      <p className="text-sm text-orange-400 font-nunito mt-1">
                        {categories.find(c => c.value === script.category)?.label}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleCopy(script.id, script.scriptText)}
                  className="px-4 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-600 transition font-nunito flex items-center gap-2"
                >
                  <span>{copiedId === script.id ? '✓' : '📋'}</span>
                  {copiedId === script.id ? 'Copied!' : 'Copy'}
                </button>
              </div>
              {isExpanded && (
                <div className="bg-black/30 border border-white/10 rounded-lg p-4 mt-4">
                  <pre className="text-orange-100 font-nunito whitespace-pre-wrap text-sm leading-relaxed">
                    {script.scriptText}
                  </pre>
                </div>
              )}
            </div>
          );
        })}

        {filteredScripts.length === 0 && (
          <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-12 text-center">
            <p className="text-orange-400 font-nunito">No scripts found in this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScriptsPage;
